import { is } from '@ms/utilities-objects/lib/is';

// Test whether or not WeakMap-based performance optimizations
// are compatible with object-freezing for integrity maintenance
// in the current browser.
// This will only be false in IE 11.
const mayStoreFrozenObjectsInWeakMaps = (() => {
  const test = {};
  const weakMap = new WeakMap();
  Object.freeze(test);
  weakMap.set(test, true);
  return !!weakMap.get(test);
})();

/**
 * @public
 */
export class Delete<TValue> {
  public readonly isDelete: true = true;

  public apply(previousValue: TValue): void {
    // Nothing. This behavior has to be implemented in Graft itself.
  }
}

/**
 * @public
 */
export interface IApplyPatchOptions {
  graft: typeof graft;
}

/**
 * @public
 */
export abstract class Patch<TValue> {
  public readonly isPatch: true = true;

  public abstract apply(previousValue: TValue, options: IApplyPatchOptions): TValue | Delete<TValue> | undefined;
}

/**
 * Patch which may be used to replace (not merge) an object in the tree with a new value.
 * @public
 */
export class ReplacePatch<TValue> extends Patch<TValue> {
  private _value: TValue;

  constructor(value: TValue) {
    super();

    this._value = value;
  }

  public apply(): TValue {
    return this._value;
  }
}

/**
 * Patch which may be used to supply a default value if the previous value in the tree is undefined.
 * @public
 */
export class DefaultPatch<TValue> extends Patch<TValue> {
  private _value: TValue;

  constructor(value: TValue) {
    super();

    this._value = value;
  }

  public apply(previousValue: TValue | undefined = this._value): TValue | undefined {
    return previousValue;
  }
}

/**
 * Patch which may be applied to supply a value only if the current value not match a given criteria.
 * @beta
 */
export class IfNotMatchPatch<TValue> extends Patch<TValue> {
  private _value: TValue;
  private _match: (value: TValue) => boolean;

  constructor(value: TValue, match: (value: TValue) => boolean) {
    super();

    this._value = value;
    this._match = match;
  }

  public apply(value: TValue): TValue {
    if (!this._match(value)) {
      return this._value;
    }

    return value;
  }
}

/**
 * Patch which may be applied to a numberic value to increment or decrement it by a set amount.
 * @beta
 */
export class IncrementPatch extends Patch<number> {
  private _increment: number;

  constructor(increment: number) {
    super();

    this._increment = increment;
  }

  public apply(value: number | undefined = 0): number {
    return value + this._increment;
  }
}

/**
 * Patch which may be used to append provided values to the end of a previous array.
 * @public
 */
export class ArrayConcatPatch<TValue> extends Patch<TValue[]> {
  private _values: TValue[];

  constructor(values: TValue[]) {
    super();

    this._values = values;
  }

  public apply(previousValues: TValue[]): TValue[] {
    const values = this._values;

    return previousValues ?
      [...previousValues, ...values] :
      [...values];
  }
}

/**
 * Patch which may be used to append new, non-matching values to the end of a previous array.
 * @public
 */
export class ArrayConcatIfNotMatchKeyPatch<TValue> extends Patch<TValue[]> {
  private _values: TValue[];
  private _getKey: (value: TValue) => string;

  constructor(values: TValue[], getKey: (value: TValue) => string) {
    super();

    this._values = values;
    this._getKey = getKey;
  }

  public apply(previousValues: TValue[]): TValue[] {
    if (!previousValues) {
      return this._values;
    }

    const previousKeys: { [key: string]: boolean; } = {};

    if (previousValues) {
      for (const value of previousValues) {
        const key = this._getKey(value);

        previousKeys[key] = true;
      }
    }

    const newValues = this._values.filter((value: TValue) => !previousKeys[this._getKey(value)]);

    return newValues.length > 0 ?
      previousValues.concat(newValues) :
      previousValues;
  }
}

/**
 * Patch which may be used to remove an exact value from a previous array.
 * @public
 */
export class ArrayRemovePatch<TValue> extends Patch<TValue[]> {
  private _value: TValue;

  constructor(value: TValue) {
    super();

    this._value = value;
  }

  public apply(previousValues: TValue[]): TValue[] {
    const value = this._value;

    return previousValues ?
      previousValues.filter((existingValue: TValue) => !is(existingValue, value)) :
      previousValues;
  }
}

/**
 * Patch which may be used to remove a matching value from a previous array.
 * @public
 */
export class ArrayRemoveMatchPatch<TValue> extends Patch<TValue[]> {
  private _match: (value: TValue) => boolean;

  constructor(match: (value: TValue) => boolean) {
    super();

    this._match = match;
  }

  public apply(previousValues: TValue[]): TValue[] {
    const match = this._match;

    return previousValues ?
      previousValues.filter((value: TValue) => !match(value)) :
      previousValues;
  }
}

/**
 * Patch which may be used to splice out matching values and insert new values in a previous array.
 * @public
 */
export class ArraySpliceMatchPatch<TValue> extends Patch<TValue[]> {
  private _values: TValue[];
  private _match: (value: TValue) => boolean;

  constructor(match: (value: TValue) => boolean, values: TValue[]) {
    super();

    this._match = match;
    this._values = values;
  }

  public apply(previousValues: TValue[]): TValue[] {
    const values = this._values;
    const match = this._match;

    let index = -1;

    for (let i = 0, length = previousValues.length; i < length; i++) {
      if (match(previousValues[i])) {
        index = i;
        break;
      }
    }

    return index > -1 ?
      [...previousValues.slice(0, index), ...values, ...previousValues.slice(index + 1)] :
      previousValues;
  }
}

/**
 * Patch which may be used to ensure all values in a previous array mirror new values,
 * preserving the original if no modifications are required.
 * @public
 */
export class ArrayMirrorPatch<TValue> extends Patch<TValue[]> {
  private _values: TValue[];

  constructor(values: TValue[]) {
    super();

    this._values = values;
  }

  public apply(previousValues: TValue[]): TValue[] {
    const values = this._values;

    if (!previousValues) {
      return values;
    }

    if (previousValues.length !== values.length) {
      return values;
    }

    for (let i = 0; i < values.length; i++) {
      if (!is(values[i], previousValues[i])) {
        return values;
      }
    }

    return previousValues;
  }
}

/**
 * Patch which may be used to make a sub-tree be an equivalent match for an input tree,
 * avoiding modifications where possible.
 * @public
 */
export class MirrorPatch<TValue extends object> extends Patch<TValue> {
  private _value: TValue | IPatch<TValue>;

  constructor(value: TValue | IPatch<TValue>) {
    super();

    this._value = value;
  }

  public apply(previousValue: TValue, options: IApplyPatchOptions): TValue | Delete<TValue> {
    const value = this._value;

    if (!previousValue ||
      !value ||
      typeof value !== 'object' ||
      Object.getPrototypeOf(value) !== Object.prototype ||
      isPatchOperation(value) || isPatchDeletion(value)) {
      return options.graft({
        value: previousValue as TValue
      }, {
          value
        }).value;
    }

    const patch: IPatch<TValue> = {};

    for (const key in previousValue) {
      if (Object.prototype.hasOwnProperty.call(previousValue, key)) {
        patch[key] = new Delete<TValue[keyof TValue]>();
      }
    }

    for (const key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        patch[key] = new MirrorPatch(value[key] as any); // tslint:disable-line:no-any
      }
    }

    return options.graft(previousValue, patch);
  }
}

/**
 * Patch which may be used to aggregate a series of patches to produce a final modification.
 * @public
 */
export class MultiPatch<TValue extends object> extends Patch<TValue> {
  private _patches: IPatch<TValue>[];

  constructor(patches: IPatch<TValue>[]) {
    super();

    this._patches = patches;
  }

  public apply(previousValue: TValue, options: IApplyPatchOptions): TValue {
    const patches = this._patches;

    const {
      graft: currentGraft
    } = options;

    return patches.reduce<TValue>((previous: TValue, next: IPatch<TValue>) => currentGraft(previous, next), previousValue);
  }
}

/**
 * Represents a patch which can be merged with an existing object to produce a new,
 * extended object.
 * @public
 */
export type IPatch<TOriginal> = {
  [K in keyof TOriginal]?: Delete<TOriginal[K]> | Patch<TOriginal[K]> | IPatch<TOriginal[K]> | TOriginal[K];
};

/**
 * @public
 */
export interface IGraftOptions {
  /**
   * Whether or not all objects put into or created within the tree should be frozen.
   * This will ensure that members of the tree are not mutated outside the tree, which
   * disrupts the ability to produce minimal updates.
   */
  freeze?: boolean;
}

/**
 * Creates a customized graft function with specified options and optimizations.
 * @param options - Options for creating the `graft` function.
 * @public
 */
export function withGraft(options: {
  freeze?: boolean;
} = {}): <TOriginal>(applyGraft: (customGraft: typeof graft) => TOriginal) => TOriginal {
  const {
    freeze = false
  } = options;

  return <TOriginal>(applyGraft: (customGraft: typeof graft) => TOriginal) => {
    // Keep track of all new objects created within this graft operation.
    // If multiple sub-grafts occur, object instance may be modified freely.
    // Once the entire graft is complete, these objects should be frozen to prevent modification.
    const newObjects = new Map<{}, true>();
    // Keep track of the current of ancestors of the graft sub-tree, to avoid traversing circular references.
    const ancestors = new Map<{}, true>();

    function graftInternal<TBase extends object>(
      base: TBase,
      extension: IPatch<TBase> | undefined): TBase {
      let result = newObjects.has(base) ? base : undefined;

      if (extension) {
        if (ancestors.has(extension)) {
          throw new Error('Encountered a circular reference in patch supplied to graft().');
        }

        ancestors.set(extension, true);

        for (const key in extension) {
          if (Object.prototype.hasOwnProperty.call(extension, key)) {
            const originalValue = base[key];
            const patchValue = extension[key];

            if (!is(patchValue, originalValue)) {
              let newValue: typeof originalValue;

              if (patchValue && isPatchDeletion(patchValue)) {
                newValue = patchValue as any; // tslint:disable-line:no-any TODO TypeScript has a bug.
              } else if (patchValue && isPatchOperation(patchValue)) {
                newValue = patchValue.apply(originalValue, {
                  graft: graftInternal
                }) as any; // tslint:disable-line:no-any TODO TypeScript has a bug.
              } else if (patchValue && typeof patchValue === 'object' && Object.getPrototypeOf(patchValue) !== Object.prototype) {
                newValue = patchValue as any; // tslint:disable-line:no-any TODO TypeScript has a bug.
              } else if (patchValue && typeof patchValue === 'object' && originalValue && typeof originalValue === 'object') {
                // @ts-ignore TypeScript has a bug in type narrowing.
                const graftValue = graftInternal(originalValue, <IPatch<TBase[keyof TBase]>>patchValue);

                newValue = graftValue;
              } else {

                // @ts-ignore TypeScript has a bug in type narrowing.
                if (typeof patchValue === 'object' && patchValue && containsPatches(patchValue)) {
                  // @ts-ignore TypeScript has a bug in type narrowing.
                  newValue = graftInternal({}, patchValue);

                  if (mayStoreFrozenObjectsInWeakMaps &&
                    freeze && newValue && typeof newValue === 'object' &&
                    !newObjects.has(newValue as any)) { // tslint:disable-line:no-any
                    if (!Object.isFrozen(newValue)) {
                      Object.freeze(newValue);
                    }
                  }
                } else {
                  newValue = patchValue as any; // tslint:disable-line:no-any TODO TypeScript has a bug.
                }
              }

              if (!is(newValue, originalValue) || !(key in base)) {
                if (!result) {
                  // Clone the original object, since it will be modified.
                  result = <TBase>{
                    ...(<object>base)
                  };

                  newObjects.set(result, true);
                }

                if (isPatchDeletion(newValue)) {
                  delete result[key];
                } else {
                  result[key] = newValue;
                }
              }
            }
          }
        }

        ancestors.delete(extension);
      }

      return result || base;
    }

    const finalResult = applyGraft(graftInternal);

    if (freeze) {
      newObjects.forEach((value: true, key: {}) => {
        if (!Object.isFrozen(key)) {
          Object.freeze(key);
        }
      });
    }

    return finalResult;
  };
}

/**
 * Grafts a patch onto an existing object, producing a new object only if strictly necessary.
 * By default, `graft` merges new keys into the resulting object and sub-objects, and overwrites existing keys
 * with primitive values.
 * Prototyped objects and arrays are not merged, and are instead overwritten whole. This is to avoid the need for
 * contextual information when handling their values. Patches may be used to supply custom merge logic
 * for prototyped objects and arrays.
 * @public
 */
export function graft<TOriginal extends object>(
  original: TOriginal,
  patch: IPatch<TOriginal> | undefined
): TOriginal {
  return withGraft()((finalGraft: typeof graft) => finalGraft(original, patch));
}

/**
 * @public
 */
export function diff<TOriginal extends object>(previous: TOriginal, current: TOriginal): IPatch<TOriginal> | undefined {
  if (is(previous, current)) {
    return undefined;
  }

  const result: IPatch<TOriginal> = {};

  for (const key in previous) {
    if (Object.prototype.hasOwnProperty.call(previous, key)) {
      const previousValue = previous[key];
      const currentValue = current[key];

      if (!(key in current)) {
        result[key] = new Delete<typeof previousValue>();
      } else if (!is(currentValue, previousValue)) {
        if (typeof currentValue === 'object' && currentValue && Object.getPrototypeOf(currentValue) !== Object.prototype) {
          result[key] = currentValue;
        } else if (typeof currentValue === 'object' && currentValue && typeof previousValue === 'object' && previousValue) {
          // @ts-ignore The type guards are not working.
          const valueDiff = diff(previousValue, currentValue);

          if (valueDiff && Object.keys(valueDiff).length > 0) {
            result[key] = valueDiff;
          }
        } else {
          result[key] = currentValue;
        }
      }
    }
  }

  for (const key in current) {
    if (Object.prototype.hasOwnProperty.call(current, key)) {
      if (!(key in previous)) {
        result[key] = current[key];
      }
    }
  }

  return result;
}

/**
 * Subtracts an object from an initial object.
 * The result is an object which contains none of the matching field and sub-fields
 * of the initial object.
 * Fields and sub-fields in the initial object which are not matched in the subracted value
 * will remain as they are in the result.
 * @public
 */
export function subtract<TValue extends object>(from: TValue, value: TValue): TValue {
  return graft(<typeof from>{}, diff(value, from));
}

/**
 * Determines whether or not an object is a strict subset of another object.
 * Grafting a strict subset into a larger object is a no-op.
 * @public
 */
export function isSubset<TValue extends object>(whole: TValue, partial: TValue): boolean {
  return is(graft(whole, partial), whole);
}

function containsPatches<TOriginal extends object>(patch: IPatch<TOriginal> | undefined): boolean {
  const ancestors = new WeakMap<{}, true>();

  function containsPatchesInternal<TBase extends object>(extension: IPatch<TBase> | undefined): boolean {
    if (extension) {
      if (ancestors.has(extension)) {
        throw new Error('Encountered circular reference in patch supplied to graft().');
      }

      ancestors.set(extension, true);

      for (const key in extension) {
        if (Object.prototype.hasOwnProperty.call(extension, key)) {
          const patchValue = extension[key];

          if (patchValue && patchValue instanceof Delete) {
            return true;
          } else if (patchValue && patchValue instanceof Patch) {
            return true;
          } else if (patchValue && typeof patchValue === 'object') {
            if (Object.getPrototypeOf(patchValue) === Object.prototype) {
              if (containsPatchesInternal(patchValue as {})) {
                return true;
              }
            }
          }
        }
      }

      ancestors.delete(extension);
    }

    return false;
  }

  return containsPatchesInternal(patch);
}

/**
 * @public
 */
export function isPatchDeletion<TValue>(
  value: Patch<TValue> | Delete<TValue> | TValue | undefined): value is Delete<TValue>;
/**
 * @public
 */
export function isPatchDeletion<TValue extends object>(
  value: Patch<TValue> | Delete<TValue> | IPatch<TValue> | TValue | undefined): value is Delete<TValue>;
/**
 * Determines whether not a value is a 'Delete' patch.
 * @internal
 */
export function isPatchDeletion<TValue extends object>(
  value: Patch<TValue> | Delete<TValue> | IPatch<TValue> | TValue | undefined): value is Delete<TValue> {
  return value instanceof Delete;
}

/**
 * @public
 */
export function isPatchOperation<TValue>(
  value: Patch<TValue> | Delete<TValue> | TValue | undefined): value is Patch<TValue>;
/**
 * @public
 */
export function isPatchOperation<TValue extends object>(
  value: Patch<TValue> | Delete<TValue> | IPatch<TValue> | TValue | undefined): value is Patch<TValue>;
/**
 * Determines whether or not a value is a Patch operation.
 * @internal
 */
export function isPatchOperation<TValue extends object>(
  value: Patch<TValue> | Delete<TValue> | IPatch<TValue> | TValue | undefined): value is Patch<TValue> {
  return value instanceof Patch;
}
